import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ModelId, GenerateRequest } from '@/types'

interface StartGenerationParams {
  modelId: ModelId
  prompt: string
  type: 'image' | 'video'
  generateParams: Omit<GenerateRequest, 'user_id'>
  /**
   * Marks the resulting generation as an unsaved character draft. The
   * success page swaps its CTAs to Save / Edit / Try Again / Discard
   * instead of the default Edit / Animate row, and propagates the flag
   * through any subsequent Edit hop so iteration stays a draft until
   * the user explicitly Saves.
   */
  isCharacterDraft?: boolean
}

/**
 * Hook to start a generation with the standardized booting flow.
 * Navigates to /generation/new which shows the booting screen
 * while the generation API call is made.
 */
export function useStartGeneration() {
  const navigate = useNavigate()

  const startGeneration = useCallback(
    ({ modelId, prompt, type, generateParams, isCharacterDraft }: StartGenerationParams) => {
      navigate('/generation/new', {
        state: {
          modelId,
          prompt,
          type,
          generateParams,
          isCharacterDraft,
        },
      })
    },
    [navigate]
  )

  return { startGeneration }
}
